import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

const DownloadGoodsReceivePdf = (data, companyName, logo, setOpenBackdrop, isListView = false, printMode = false, orientation = 'portrait') => {
    if (!data || data.length === 0) {
        alert("No data found to generate report!");
        return;
    }

    const doc = new jsPDF({ orientation: orientation === 'landscape' ? 'landscape' : 'portrait' });
    setOpenBackdrop(true);

    const safeValue = (value) => value !== undefined && value !== null && value !== '' ? value : 'N/A';

    try {
        let currentY = 15;
        const pageWidth = doc.internal.pageSize.getWidth();

        // --- Header Section ---
        if (logo) doc.addImage(logo, 'PNG', 14, 8, 12, 12);

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(companyName || 'COMPANY NAME', logo ? 28 : 14, 16);

        // Check data type first to determine title
        const isAggregatedData = data.length > 0 && data[0].hasOwnProperty('item_name') && data[0].hasOwnProperty('total_quantity');
        const isListViewData = isListView || (data.length > 0 && data[0].hasOwnProperty('bill_no') && !data[0].hasOwnProperty('items'));

        doc.setFontSize(16);
        doc.setTextColor(25, 118, 210);
        doc.setFont("helvetica", "bold");
        const title = isAggregatedData
            ? 'ITEMS SUMMARY REPORT'
            : isListViewData
                ? 'GOODS RECEIVE LIST REPORT'
                : 'GOODS RECEIVE REPORT';
        doc.text(title, (pageWidth - doc.getTextWidth(title)) / 2, 26);

        doc.setLineWidth(0.5);
        doc.line(14, 30, 196, 30);
        currentY = 40;

        if (isAggregatedData) {
            // Aggregated items summary
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont("helvetica", "bold");
            doc.text('Item Summary Report', 14, currentY);
            currentY += 10;

            // Table headers
            const tableHeaders = [['SL No.', 'Item Name', 'Total Quantity Received']];
            const tableData = data.map((item, index) => [
                index + 1,
                item.item_name || 'N/A',
                item.total_quantity || 0
            ]);

            doc.autoTable({
                startY: currentY,
                head: tableHeaders,
                body: tableData,
                theme: 'grid',
                headStyles: { fillColor: [70, 70, 70], fontSize: 10, textColor: 255 },
                bodyStyles: { fontSize: 9 },
                columnStyles: {
                    0: { cellWidth: 20, halign: 'center' },
                    1: { cellWidth: 120 },
                    2: { cellWidth: 40, halign: 'center' }
                },
            });

            currentY = doc.lastAutoTable.finalY + 10;

            // Summary
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(`Total Items: ${data.length}`, 14, currentY);
            doc.text(`Total Quantity: ${data.reduce((sum, item) => sum + (item.total_quantity || 0), 0)}`, 100, currentY);
        } else if (isListViewData) {
            // List view with master data and item details per GRN
            doc.setFontSize(12);
            doc.setTextColor(0);
            doc.setFont("helvetica", "bold");
            // doc.text('Goods Receive List Report', 14, currentY);
            currentY += 12;

            data.forEach((receipt, receiptIndex) => {
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                const safeBillNo = receipt.purchase_bill_no || receipt.bill_no || 'N/A';
                const safeOrderNo = receipt.purchase_order_no || receipt.order_number || receipt.order_no || 'N/A';
                const safeSupplier = receipt.supplier_name || receipt.supplier || receipt.vendor || receipt.vendor_name || 'N/A';
                const safeReceiveAt = receipt.location_address || receipt.receive_at || receipt.location || 'N/A';
                const safeReceiveBy = receipt.staff_name || receipt.receive_by || receipt.received_by || receipt.received_name || 'N/A';
                const safeReceiveDate = receipt.billing_date || receipt.receive_date || receipt.receipt_date || receipt.delivery_date;
                const safeReceiveDateText = safeReceiveDate ? dayjs(safeReceiveDate).format("DD/MM/YYYY") : 'N/A';

                doc.text(`GRN No: ${receipt.grn_no || 'N/A'}`, 14, currentY);
                doc.text(`Bill No: ${safeBillNo}`, 90, currentY);
                doc.text(`Order No: ${safeOrderNo}`, 190, currentY, { align: 'right' });
                currentY += 6;

                doc.setFontSize(9);
                doc.setFont("helvetica", "normal");
                doc.text(`Supplier: ${safeSupplier}`, 14, currentY);
                doc.text(`Receive At: ${safeReceiveAt}`, 90, currentY);
                doc.text(`Receive By: ${safeReceiveBy}`, 190, currentY, { align: 'right' });
                currentY += 6;
                doc.text(`Receive Date: ${safeReceiveDateText}`, 14, currentY);
                currentY += 8;

                const masterTableHeaders = [['Field', 'Value']];
                const masterTableData = [
                    ['GRN No', receipt.grn_no || 'N/A'],
                    ['Bill No', safeBillNo],
                    ['Order No', safeOrderNo],
                    ['Supplier', safeSupplier],
                    ['Receive At', safeReceiveAt],
                    ['Receive By', safeReceiveBy],
                    ['Receive Date', safeReceiveDateText],
                ];

                doc.autoTable({
                    startY: currentY,
                    head: masterTableHeaders,
                    body: masterTableData,
                    theme: 'grid',
                    headStyles: { fillColor: [70, 70, 70], fontSize: 8, textColor: 255 },
                    bodyStyles: { fontSize: 8 },
                    columnStyles: { 0: { cellWidth: 40, fontStyle: 'bold' }, 1: { cellWidth: 140 } },
                    margin: { left: 14, right: 14 },
                });

                currentY = doc.lastAutoTable.finalY + 8;

                if (receipt.items && receipt.items.length > 0) {
                    if (currentY > 230) {
                        doc.addPage();
                        currentY = 20;
                    }

                    doc.setFontSize(10);
                    doc.setFont("helvetica", "bold");
                    doc.text('Items', 14, currentY);
                    currentY += 6;

                    const itemHeaders = [[
                        'SL No.',
                        'Item Name',
                        'Item Code',
                        'HSN Code',
                        'Order Qty',
                        'Received Qty',
                        'Rejected Qty',
                        'Actual Qty',
                        'Unit'
                    ]];

                    const itemData = receipt.items.map((item, index) => {
                        const safeItemName = item.item_name || 'N/A';
                        const safeItemCode = item.item_code || 'N/A';
                        const safeHsn = item.hsn_code || item.hsncode || item.hsn || 'N/A';
                        return [
                            index + 1,
                            safeItemName,
                            safeItemCode,
                            safeHsn,
                            item.order_qty || 0,
                            item.received_qty || 0,
                            item.rejected_qty || 0,
                            (parseFloat(item.received_qty || 0) - parseFloat(item.rejected_qty || 0)) || 0,
                            item.unit || 'N/A'
                        ];
                    });

                    doc.autoTable({
                        startY: currentY,
                        head: itemHeaders,
                        body: itemData,
                        theme: 'grid',
                        headStyles: { fillColor: [70, 70, 70], fontSize: 7, textColor: 255 },
                        bodyStyles: { fontSize: 7 },
                        columnStyles: {
                            0: { cellWidth: 12, halign: 'center' },
                            1: { cellWidth: 45 },
                            2: { cellWidth: 25 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 18, halign: 'center' },
                            5: { cellWidth: 18, halign: 'center' },
                            6: { cellWidth: 18, halign: 'center' },
                            7: { cellWidth: 18, halign: 'center' },
                            8: { cellWidth: 18, halign: 'center' }
                        },
                        margin: { left: 14, right: 14 },
                    });

                    currentY = doc.lastAutoTable.finalY + 10;
                } else {
                    currentY += 10;
                }

                if (receiptIndex < data.length - 1) {
                    currentY += 2;
                }
            });

            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.text(`Total Records: ${data.length}`, 14, currentY);
        } else {
            // Original detailed report for individual GRN
            data.forEach((receipt) => {
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                // Safe field mappings
                const safeBillNo = receipt.purchase_bill_no || receipt.bill_no || 'N/A';
                const safeSupplier = receipt.supplier_name || receipt.supplier || receipt.vendor || receipt.vendor_name || 'N/A';
                const safeLocation = receipt.location_address || receipt.location || receipt.receive_at || 'N/A';
                const safeReceivedBy = receipt.staff_name || receipt.receive_by || receipt.received_by || receipt.received_name || 'N/A';
                const safeOrderNo = receipt.purchase_order_no || receipt.order_number || receipt.order_no || 'N/A';
                const safeBillingDate = receipt.billing_date || receipt.receive_date || receipt.receipt_date || receipt.delivery_date;
                const safeBillingDateText = safeBillingDate ? dayjs(safeBillingDate).format("DD/MM/YYYY") : 'N/A';

                // Receipt Header (GRN Details)
                doc.setFillColor(240, 244, 248);
                doc.rect(14, currentY, 182, 18, 'F');
                doc.setFontSize(9);
                doc.setTextColor(0);

                doc.setFont("helvetica", "bold");
                doc.text(`GRN No: ${receipt.grn_no}`, 18, currentY + 7);
                doc.text(`Bill No: ${safeBillNo}`, 80, currentY + 7);
                doc.text(`Received Date: ${safeBillingDateText}`, 192, currentY + 7, { align: 'right' });

                doc.setFont("helvetica", "normal");
                doc.text(`Supplier: ${safeSupplier}`, 18, currentY + 14);
                doc.text(`Received At: ${safeLocation}`, 80, currentY + 14);
                doc.setTextColor(200, 0, 0);
                doc.text(`Approved: ${receipt.is_approved || 'N/A'}`, 192, currentY + 14, { align: 'right' });
                doc.setTextColor(0);

                currentY += 22;

                // Summary Information
                doc.setFontSize(8);
                doc.setFont("helvetica", "bold");
                doc.text(`Received By: ${safeReceivedBy}`, 18, currentY);
                doc.text(`Order No: ${safeOrderNo}`, 18, currentY + 5);
                currentY += 12;

                // Simple summary table
                const tableHeaders = [['Field', 'Value']];
                const tableData = [
                    ['GRN No', receipt.grn_no],
                    ['Bill No', safeBillNo],
                    ['Order No', safeOrderNo],
                    ['Supplier', safeSupplier],
                    ['Location', safeLocation],
                    ['Received By', safeReceivedBy],
                    ['Received Date', safeBillingDateText],
                    ['Approved Status', receipt.is_approved || 'N/A'],
                ];

                doc.autoTable({
                    startY: currentY,
                    head: tableHeaders,
                    body: tableData,
                    theme: 'grid',
                    headStyles: { fillColor: [70, 70, 70], fontSize: 8, textColor: 255 },
                    bodyStyles: { fontSize: 8 },
                    columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold' }, 1: { cellWidth: 70 } },
                });

                currentY = doc.lastAutoTable.finalY + 10;

                if (receipt.items && receipt.items.length > 0) {
                    if (currentY > 230) {
                        doc.addPage();
                        currentY = 20;
                    }

                    doc.setFontSize(10);
                    doc.setFont("helvetica", "bold");
                    doc.text('Items', 14, currentY);
                    currentY += 6;

                    const itemHeaders = [[
                        'SL No.',
                        'Item Name',
                        'Item Code',
                        'HSN Code',
                        'Order Qty',
                        'Received Qty',
                        'Rejected Qty',
                        'Actual Qty',
                        'Unit'
                    ]];
                    const itemData = receipt.items.map((item, index) => {
                        const safeItemName = item.item_name || item.itemName || item.name || 'N/A';
                        const safeItemCode = item.item_code || item.itemCode || item.code || 'N/A';
                        const safeHsn = item.hsn_code || item.hsncode || item.hsn || 'N/A';
                        return [
                            index + 1,
                            safeItemName,
                            safeItemCode,
                            safeHsn,
                            item.order_qty || 0,
                            item.received_qty || 0,
                            item.rejected_qty || 0,
                            (parseFloat(item.received_qty || 0) - parseFloat(item.rejected_qty || 0)) || 0,
                            item.unit || 'N/A'
                        ];
                    });

                    doc.autoTable({
                        startY: currentY,
                        head: itemHeaders,
                        body: itemData,
                        theme: 'grid',
                        headStyles: { fillColor: [70, 70, 70], fontSize: 7, textColor: 255 },
                        bodyStyles: { fontSize: 7 },
                        columnStyles: {
                            0: { cellWidth: 12, halign: 'center' },
                            1: { cellWidth: 45 },
                            2: { cellWidth: 25 },
                            3: { cellWidth: 20 },
                            4: { cellWidth: 18, halign: 'center' },
                            5: { cellWidth: 18, halign: 'center' },
                            6: { cellWidth: 18, halign: 'center' },
                            7: { cellWidth: 18, halign: 'center' },
                            8: { cellWidth: 18, halign: 'center' }
                        },
                        margin: { left: 14, right: 14 },
                    });

                    currentY = doc.lastAutoTable.finalY + 10;
                }
            });
        }

        const fileName = isAggregatedData ? `Goods_Receive_Items_Summary_${new Date().getTime()}.pdf` :
            isListViewData ? `Goods_Receive_List_${new Date().getTime()}.pdf` :
                `Goods_Receive_${new Date().getTime()}.pdf`;
        if (printMode) {
            const url = doc.output('dataurlstring');
            window.open(url);
        } else {
            doc.save(fileName);
        }
    } catch (error) {
        console.error("PDF Error:", error);
        alert("Error generating PDF!");
    } finally {
        setOpenBackdrop(false);
    }
};

export default DownloadGoodsReceivePdf;
